<template>
  <Teleport to="body">
    <transition name="undo-toast-fade" @after-leave="onTransitionDone">
      <div
        v-if="visible"
        role="status"
        aria-live="polite"
        class="fixed top-5 right-5 z-[9999] bg-white border border-gray-200 border-l-[3px] border-l-blue-500 rounded-md shadow-lg px-5 py-3.5 font-['IBM_Plex_Sans'] text-sm text-gray-700 flex items-center gap-4 min-w-[200px]"
        :class="{ '!border-l-green-500': confirmed, '!border-l-red-500': error }"
      >
        <span class="grow">{{ error ? 'Save failed' : confirmed ? 'Change undone' : message }}</span>
        <button
          v-if="error"
          class="font-semibold text-red-500 hover:text-red-700 tracking-wide shrink-0"
          @click="retry"
        >
          RETRY
        </button>
        <button
          v-else-if="!confirmed"
          class="font-semibold text-blue-500 hover:text-blue-700 tracking-wide shrink-0"
          @click="onUndo"
        >
          UNDO
        </button>
        <button
          class="text-gray-400 hover:text-gray-600 shrink-0 w-4 h-4 flex items-center justify-center"
          aria-label="Dismiss"
          @click="onClose"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          >
            <line x1="1" y1="1" x2="9" y2="9" />
            <line x1="9" y1="1" x2="1" y2="9" />
          </svg>
        </button>
      </div>
    </transition>
  </Teleport>
</template>

<script>
export default {
  name: 'Support_UndoToast',
  props: {
    duration: { type: Number, default: 5000 },
  },
  data() {
    return {
      visible: false,
      message: '',
      confirmed: false,
      error: false,
      settled: false,
      commitTimer: null,
      commitFn: null,
      undoFn: null,
    };
  },
  beforeUnmount() {
    this.commitPending();
    clearTimeout(this.commitTimer);
  },
  methods: {
    // One undoable action at a time: starting a new one commits the previous
    // immediately (Gmail / Snackbar semantics). The cost is that the first
    // edit loses its undo window, which the caller's per-field Saving.../Saved
    // badge surfaces. No Service Desk write has an external side effect today
    // -- emails go out on ticket creation and on comments, not on status or
    // field changes -- so committing early is invisible outside the app. If
    // that ever changes, this needs a queue rather than a single slot.
    show(message, commitFn, undoFn) {
      this.commitPending();
      this.message = message;
      this.commitFn = commitFn;
      this.undoFn = undoFn;
      this.settled = false;
      this.confirmed = false;
      this.error = false;
      this.visible = true;
      this.commitTimer = setTimeout(async () => {
        if (this.settled) return;
        this.settled = true;
        if (await this.execCommit()) this.hide();
      }, this.duration);
    },
    async execCommit() {
      try {
        await this.commitFn?.();
        return true;
      } catch {
        this.error = true;
        this.visible = true;
        return false;
      }
    },
    commitPending() {
      if (!this.settled && this.commitFn) {
        this.settled = true;
        clearTimeout(this.commitTimer);
        Promise.resolve(this.commitFn()).catch(() => {});
      }
    },
    async retry() {
      this.error = false;
      if (await this.execCommit()) this.hide();
    },
    onUndo() {
      if (this.settled) return;
      this.settled = true;
      clearTimeout(this.commitTimer);
      this.undoFn?.();
      this.confirmed = true;
      setTimeout(() => this.hide(), 2000);
    },
    async onClose() {
      if (this.error) {
        // Dismissing a failed save must roll the optimistic change back.
        // Otherwise the row keeps showing a value the backend never received
        // and the only warning about it has just been dismissed, so the user
        // walks away believing it saved. Closing the error is a decision not
        // to retry, so treat it as an undo.
        this.undoFn?.();
        this.hide();
        return;
      }
      if (!this.settled) {
        this.settled = true;
        clearTimeout(this.commitTimer);
        if (!await this.execCommit()) return;
      }
      this.hide();
    },
    hide() {
      this.visible = false;
    },
    onTransitionDone() {
      this.commitFn = null;
      this.undoFn = null;
    },
  },
};
</script>

<style scoped>
.undo-toast-fade-enter-active,
.undo-toast-fade-leave-active {
  transition: opacity 0.25s ease;
}
.undo-toast-fade-enter-from,
.undo-toast-fade-leave-to {
  opacity: 0;
}
</style>
